import type { InputNumberRef } from '@rc-component/input-number';
import { Button as AntdButton, Segmented, Select, Tooltip } from 'antd';
import LinkButton from 'components/common/LinkButton/LinkButton';
import ColumnVisibilityControl from 'components/reusable/ColumnVisibilityControl';
import NumericInputField from 'components/common/NumericInputField';
import {
  FARM_DIAGNOSTICS_COST_DISPLAY_PER_HA_LABEL,
  FARM_DIAGNOSTICS_COST_DISPLAY_TOTAL_LABEL,
  FARM_DIAGNOSTICS_INCOME_UNIT_SUFFIX,
  INPUT_DOC_TAB_PLANNING,
  LINK_OPEN_PLAN_LABEL,
  FARM_DIAGNOSTICS_TOTAL_ROW_AVG_LABEL,
  FARM_DIAGNOSTICS_TOTAL_ROW_AVG_TOOLTIP,
  FARM_DIAGNOSTICS_CROP_LABEL,
  FARM_DIAGNOSTICS_PRACTICE_LABELS,
  FARM_DIAGNOSTICS_RESET_ALL_LABEL,
  FARM_DIAGNOSTICS_RESET_YEAR_LABEL,
  FARM_DIAGNOSTICS_SECTION_COLLAPSE_LABEL,
  FARM_DIAGNOSTICS_SECTION_EXPAND_LABEL,
  FARM_DIAGNOSTICS_SOURCE_TOOLTIP,
  FARM_DIAGNOSTICS_TABLE_COLLAPSED_HINT,
  FARM_DIAGNOSTICS_TABLE_SHARED_SOURCE_SUBTITLE,
  FARM_DIAGNOSTICS_TOTAL_COST_TOOLTIP,
  FARM_DIAGNOSTICS_YIELD_FIELD_LABEL,
  FARM_YEAR_INPUTS_PRACTICE_LABEL,
  FARM_YEAR_INPUTS_PRACTICE_SELECT_PLACEHOLDER,
  FARM_YEAR_INPUTS_RAINFALL_LABEL,
  FARM_YEAR_INPUTS_RAINFALL_REQUIRED_TOOLTIP,
  SUMMARY_ENTITY_LABELS,
} from 'constants/uiConstants';
import { InputTaskCategory } from 'interfaces/IInputTask';
import React, { useMemo, useRef, useState } from 'react';
import { LuChevronDown, LuChevronUp } from 'react-icons/lu';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/Store';
import { PADDOCK_KEY_PREFIX } from 'constants/routeConstants';
import { formatCurrency } from 'utils/CurrencyUtils';
import type { SummaryEntityCategory } from '../../../../redux/slices/InsightsSlice';
import {
  clearAllFarmDiagnosticsOverridesForPaddock,
  clearFarmDiagnosticsYearOverrides,
  setFarmDiagnosticsCostOverride,
  setFarmDiagnosticsYieldOverride,
  setFarmYearPracticeTags,
  setFarmYearRainfall,
  type PracticeTag,
} from '../../../../redux/slices/InsightsSlice';
import useFarmsLinkNavigate from 'pages/FarmsPage/hooks/useFarmsLinkNavigate';
import { formatNumber } from '../Insights/summary2/formatNumber';
import SectionCard from '../Insights/summary2/SectionCard';
import {
  FARM_DIAGNOSTICS_COST_CATEGORY_ORDER,
  resolvePracticeTagsAfterChange,
} from './computeFarmDiagnosticsMetrics';
import type { WueTrendYearRow } from './computeWueTrend';

const CATEGORY_OPTIONS = [
  { label: 'Actual', value: InputTaskCategory.Actual },
  { label: 'Plan', value: InputTaskCategory.Plan },
];

const PRACTICE_OPTIONS = (
  Object.keys(FARM_DIAGNOSTICS_PRACTICE_LABELS) as PracticeTag[]
).map((value) => ({ value, label: FARM_DIAGNOSTICS_PRACTICE_LABELS[value] }));

const COST_PRECISION = 2;
const COST_STEP = 0.01;

type CostDisplayMode = 'total' | 'perHa';

const COST_DISPLAY_OPTIONS: { value: CostDisplayMode; label: string }[] = [
  { value: 'perHa', label: FARM_DIAGNOSTICS_COST_DISPLAY_PER_HA_LABEL },
  { value: 'total', label: FARM_DIAGNOSTICS_COST_DISPLAY_TOTAL_LABEL },
];

/** Every column the "Columns" picker can individually show/hide — Year, Rainfall, Management Practice and Source are always shown (row identifiers, not optional data). */
type FarmDiagnosticsColumnKey = 'production' | 'income' | 'totalCost' | SummaryEntityCategory;

const FARM_DIAGNOSTICS_COLUMN_ORDER: FarmDiagnosticsColumnKey[] = [
  'production',
  'income',
  'totalCost',
  ...FARM_DIAGNOSTICS_COST_CATEGORY_ORDER,
];

const FARM_DIAGNOSTICS_COLUMN_GROUPS = [
  { label: 'Costs', keys: FARM_DIAGNOSTICS_COST_CATEGORY_ORDER },
];

// Every cost category (an "input category" — Seed, Fertiliser, etc.) starts
// hidden, keeping the table to just what's needed to drive the charts
// (Production, Income, Plan/Actual, Yield) until a user asks for more detail.
// Total Cost stays visible by default — it's a read-only rollup, not one of
// the per-category inputs this default is protecting the user from.
const DEFAULT_HIDDEN_COLUMNS: FarmDiagnosticsColumnKey[] = FARM_DIAGNOSTICS_COST_CATEGORY_ORDER;

const getFarmDiagnosticsColumnLabel = (key: FarmDiagnosticsColumnKey): string => {
  if (key === 'production') return 'Production';
  if (key === 'income') return 'Income';
  if (key === 'totalCost') return 'Total Cost';
  return SUMMARY_ENTITY_LABELS[key];
};

interface FarmDiagnosticsTableProps {
  paddockId: string;
  paddockName?: string;
  farmId: string;
  farmName?: string;
  /** Epic 1's own per-year rows — sparse (a true gap year, e.g. Fallow with no Input doc, has no entry here). */
  rows: WueTrendYearRow[];
  /** Full contiguous calendar-year range for the selected window (superset of `rows`' years) — every year gets a row so rainfall/practice can still be entered for a gap year even with no Epic 1 data. */
  years: number[];
  /** Per-year Plan/Actual selection — falls back to each row's own defaultCategory when absent. Also drives what useFarmDiagnosticsSeries computes, so this table is the single source of truth for every downstream chart. */
  selectedCategory: Record<number, InputTaskCategory>;
  onCategoryChange: (year: number, category: InputTaskCategory) => void;
}

const NoDataCell: React.FC<{ reason: string }> = ({ reason }) => (
  <Tooltip title={reason}>
    <span className="cursor-help font-normal text-gray-muted">—</span>
  </Tooltip>
);

/**
 * Combined per-year table for the three per-paddock Farm Diagnostics lenses
 * — one row per calendar year with two column groups, visually separated by
 * a divider: farm-level rainfall/management-practice (shared by every
 * paddock on this farm) on the left, and this paddock's own Plan/Actual
 * toggle + editable yield/cost fields (prefilled from Epic 1) on the right.
 * Session-only; nothing here is ever persisted to localStorage or a backend.
 *
 * This table — every field in it — is the single data source
 * `useFarmDiagnosticsSeries` reads from — every chart across every lens
 * reflects whatever is selected/edited here, rainfall and practice included.
 *
 * Gross Margin, Cost % Income and Total Costs are deliberately not shown —
 * this table is about the inputs that drive the WUE/Financial dashboards,
 * not a margin report (Cost by Crop in the Summary tab already covers that).
 */
const FarmDiagnosticsTable: React.FC<FarmDiagnosticsTableProps> = ({
  paddockId,
  paddockName,
  farmId,
  farmName,
  rows,
  years,
  selectedCategory,
  onCategoryChange,
}) => {
  const dispatch = useDispatch();
  const navigateToFarmsView = useFarmsLinkNavigate();
  const overrides = useSelector(
    (state: RootState) => state.insightsReducer.farmDiagnosticsOverrides[paddockId]
  );
  const farmYearInputs = useSelector(
    (state: RootState) => state.insightsReducer.farmYearInputs[farmId]
  );
  // Expanded by default — this section now contains the farm-level inputs
  // that need the user's own entry, not just prefilled Epic 1 figures.
  const [expanded, setExpanded] = useState(true);
  const [costDisplayMode, setCostDisplayMode] = useState<CostDisplayMode>('perHa');
  const [hiddenColumns, setHiddenColumns] = useState<FarmDiagnosticsColumnKey[]>(
    DEFAULT_HIDDEN_COLUMNS
  );
  // Indexed by row position — lets ArrowUp/ArrowDown move focus to the
  // adjacent row's rainfall field; the onKeyDown on each NumericInputField
  // calls event.preventDefault() and uses this ref array to navigate.
  const rainfallInputRefs = useRef<(InputNumberRef | null)[]>([]);

  const hiddenColumnSet = new Set(hiddenColumns);
  const showProduction = !hiddenColumnSet.has('production');
  const showIncome = !hiddenColumnSet.has('income');
  const showTotalCost = !hiddenColumnSet.has('totalCost');
  const visibleCostCategories = useMemo(
    () => FARM_DIAGNOSTICS_COST_CATEGORY_ORDER.filter((c) => !hiddenColumnSet.has(c)),
    [hiddenColumnSet]
  );

  const handleToggleColumn = (key: FarmDiagnosticsColumnKey) =>
    setHiddenColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  const hasAnyOverride = useMemo(
    () =>
      Object.values(overrides ?? {}).some(
        (yearOverride) =>
          yearOverride.yieldOverride != null ||
          Object.keys(yearOverride.costOverrides ?? {}).length > 0
      ),
    [overrides]
  );

  const resolved = useMemo(
    () =>
      rows.map((row) => {
        const activeCategory = selectedCategory[row.year] ?? row.defaultCategory;
        const result =
          activeCategory === InputTaskCategory.Actual ? row.actual : row.plan;
        const yearOverride = overrides?.[row.year];
        const effectiveYield = result
          ? (yearOverride?.yieldOverride ?? result.yieldValue)
          : null;
        const production =
          result && effectiveYield != null ? effectiveYield * result.areaHa : null;
        const income =
          result && effectiveYield != null
            ? effectiveYield * result.pricePerUnit
            : null;
        // Sums the EFFECTIVE (post-override) cost of every category, always
        // — regardless of which category columns are currently toggled on —
        // so Total Cost reflects the true current total, not a partial sum
        // of just whatever happens to be visible.
        const totalCost = result
          ? FARM_DIAGNOSTICS_COST_CATEGORY_ORDER.reduce(
              (sum, category) =>
                sum + (yearOverride?.costOverrides?.[category] ?? result.costsTotal[category] ?? 0),
              0
            )
          : null;
        return {
          row,
          activeCategory,
          result,
          yearOverride,
          effectiveYield,
          production,
          income,
          totalCost,
        };
      }),
    [rows, selectedCategory, overrides]
  );

  const resolvedByYear = useMemo(() => {
    const map = new Map<number, (typeof resolved)[number]>();
    for (const entry of resolved) map.set(entry.row.year, entry);
    return map;
  }, [resolved]);

  const totals = useMemo(() => {
    const byCategory: Partial<Record<SummaryEntityCategory, number>> = {};
    let areaHa = 0;
    let production = 0;
    let incomeDollars = 0;
    let incomeAreaHa = 0;
    let totalCost = 0;
    for (const {
      result,
      yearOverride,
      production: rowProduction,
      income: rowIncome,
      totalCost: rowTotalCost,
    } of resolved) {
      if (!result) continue;
      areaHa += result.areaHa;
      production += rowProduction ?? 0;
      // Income is $/ha per row, so it's weighted by area, not summed.
      if (rowIncome != null) {
        incomeDollars += rowIncome * result.areaHa;
        incomeAreaHa += result.areaHa;
      }
      totalCost += rowTotalCost ?? 0;
      // Effective (post-override) cost, the same basis as each row's cell.
      for (const category of FARM_DIAGNOSTICS_COST_CATEGORY_ORDER) {
        byCategory[category] =
          (byCategory[category] ?? 0) +
          (yearOverride?.costOverrides?.[category] ??
            result.costsTotal[category] ??
            0);
      }
    }
    return {
      byCategory,
      areaHa,
      production,
      incomeAvgPerHa: incomeAreaHa > 0 ? incomeDollars / incomeAreaHa : 0,
      totalCost,
    };
  }, [resolved]);

  // The Total row matches the column header: $ in Total ($) mode, and the
  // area-weighted average $/ha in $/ha mode (Σ$ ÷ Σha).
  const isCostPerHaMode = costDisplayMode === 'perHa';
  const toFooterCost = (dollars: number): number =>
    isCostPerHaMode ? (totals.areaHa > 0 ? dollars / totals.areaHa : 0) : dollars;
  const avgBadge = isCostPerHaMode ? (
    <Tooltip title={FARM_DIAGNOSTICS_TOTAL_ROW_AVG_TOOLTIP}>
      <span className="ml-1 cursor-help text-xs font-normal text-gray-muted">
        {FARM_DIAGNOSTICS_TOTAL_ROW_AVG_LABEL}
      </span>
    </Tooltip>
  ) : null;

  const costColumnSuffix =
    costDisplayMode === 'total'
      ? FARM_DIAGNOSTICS_COST_DISPLAY_TOTAL_LABEL
      : FARM_DIAGNOSTICS_COST_DISPLAY_PER_HA_LABEL;

  // Paddock-side column count, for the group-label row's colSpan — Source,
  // Ha, Yield, optional Production/Income/Total Cost, every VISIBLE cost
  // category, and Reset.
  const paddockColCount =
    4 +
    (showProduction ? 1 : 0) +
    (showIncome ? 1 : 0) +
    (showTotalCost ? 1 : 0) +
    visibleCostCategories.length;

  if (years.length === 0) return null;

  return (
    <SectionCard
      id="farmDiagnostics-yearlyOverview"
      title="Yearly Overview"
      variant="config"
      collapsed={!expanded}
      subtitle={FARM_DIAGNOSTICS_TABLE_SHARED_SOURCE_SUBTITLE}
      actions={
        <>
          {/* Switches Total Cost and every category column (and their Total
              row), so it shows whenever any of those columns is visible. */}
          {expanded && (showTotalCost || visibleCostCategories.length > 0) && (
            <Segmented<CostDisplayMode>
              size="small"
              options={COST_DISPLAY_OPTIONS}
              value={costDisplayMode}
              onChange={setCostDisplayMode}
            />
          )}
          {expanded && (
            // ml-auto here (not on the expand/collapse button) pins this and
            // everything after it — Columns, then expand/collapse — to the
            // RH side as one cluster, consistent with every other
            // collapsible section in Farm Diagnostics.
            <ColumnVisibilityControl<FarmDiagnosticsColumnKey>
              className="ml-auto"
              columns={FARM_DIAGNOSTICS_COLUMN_ORDER}
              getLabel={getFarmDiagnosticsColumnLabel}
              groups={FARM_DIAGNOSTICS_COLUMN_GROUPS}
              hidden={hiddenColumns}
              onToggle={handleToggleColumn}
              onSetHidden={setHiddenColumns}
              onShowAll={() => setHiddenColumns([])}
            />
          )}
          <button
            type="button"
            className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-transparent bg-transparent px-2.5 py-1 text-sm font-semibold text-charcoal transition-colors hover:border-charcoal hover:bg-rowHover ${expanded ? '' : 'ml-auto'}`}
            onClick={() => setExpanded((prev) => !prev)}
          >
            {expanded ? <LuChevronUp size={14} /> : <LuChevronDown size={14} />}
            {expanded
              ? FARM_DIAGNOSTICS_SECTION_COLLAPSE_LABEL
              : FARM_DIAGNOSTICS_SECTION_EXPAND_LABEL}
          </button>
        </>
      }
    >
      {/* text-gray-dark, not gray-400 — this is the only visible content while collapsed, and gray-400 falls under WCAG AA contrast. */}
      {!expanded ? (
        <span className="text-sm text-gray-dark">{FARM_DIAGNOSTICS_TABLE_COLLAPSED_HINT}</span>
      ) : (
      <div className="summary-grid-scroll w-0 min-w-full overflow-x-auto">
        <table className="w-max border-collapse text-base">
          <thead>
            <tr>
              <th colSpan={3} className="pb-1 pl-2 text-left text-sm font-semibold text-gray-muted">
                {farmName}
              </th>
              <th
                colSpan={paddockColCount}
                className="border-l-2 border-gray-bright pb-1 pl-4 text-left text-sm font-semibold text-gray-muted"
              >
                {`Displaying: ${paddockName ?? ''}`}
              </th>
            </tr>
            <tr>
              <th className="w-16 pb-2 pl-2 pr-4 text-left text-sm font-bold text-charcoal">
                Year
              </th>
              <th className="w-24 px-2 pb-2 text-left text-sm font-bold text-charcoal">
                <Tooltip title={FARM_YEAR_INPUTS_RAINFALL_REQUIRED_TOOLTIP}>
                  <span className="cursor-help">
                    {FARM_YEAR_INPUTS_RAINFALL_LABEL} <span className="text-danger">*</span>
                  </span>
                </Tooltip>
              </th>
              <th className="w-44 px-2 pb-2 text-left text-sm font-bold text-charcoal">
                {FARM_YEAR_INPUTS_PRACTICE_LABEL}
              </th>
              <th className="w-32 border-l-2 border-gray-bright px-2 pb-2 pl-4 text-left text-sm font-bold text-charcoal">
                <Tooltip title={FARM_DIAGNOSTICS_SOURCE_TOOLTIP}>
                  <span className="cursor-help">Source</span>
                </Tooltip>
              </th>
              <th className="w-20 px-2 pb-2 text-right text-sm font-bold text-charcoal">
                Ha
              </th>
              <th className="w-28 px-2 pb-2 text-left text-sm font-bold text-charcoal">
                {FARM_DIAGNOSTICS_YIELD_FIELD_LABEL} (/ha)
              </th>
              {showProduction && (
                <th className="w-24 px-2 pb-2 text-right text-sm font-bold text-charcoal">
                  Production
                </th>
              )}
              {showIncome && (
                <th className="w-24 px-2 pb-2 text-right text-sm font-bold text-charcoal">
                  Income {FARM_DIAGNOSTICS_INCOME_UNIT_SUFFIX}
                </th>
              )}
              {showTotalCost && (
                <th className="w-28 px-2 pb-2 text-right text-sm font-bold text-charcoal">
                  Total Cost ({costColumnSuffix})
                </th>
              )}
              {visibleCostCategories.map((category) => (
                <th
                  key={category}
                  className="w-36 px-2 pb-2 text-left text-sm font-bold text-charcoal"
                >
                  {SUMMARY_ENTITY_LABELS[category]} ({costColumnSuffix})
                </th>
              ))}
              <th className="w-24 px-2 pb-2 text-left text-sm font-bold text-charcoal">
                {hasAnyOverride && (
                  <AntdButton
                    size="small"
                    onClick={() =>
                      dispatch(clearAllFarmDiagnosticsOverridesForPaddock({ paddockId }))
                    }
                  >
                    {FARM_DIAGNOSTICS_RESET_ALL_LABEL}
                  </AntdButton>
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            {years.map((year, idx) => {
              const entry = resolvedByYear.get(year);
              const { row, activeCategory, result, yearOverride, production, income, totalCost } =
                entry ?? {};
              const totalCostDisplay =
                totalCost == null
                  ? null
                  : costDisplayMode === 'total'
                    ? totalCost
                    : result && result.areaHa > 0
                      ? totalCost / result.areaHa
                      : 0;
              const hasOverride =
                !!yearOverride &&
                (yearOverride.yieldOverride != null ||
                  Object.keys(yearOverride.costOverrides ?? {}).length > 0);
              const rainfallMm = farmYearInputs?.[year]?.rainfallMm ?? null;
              const practiceTags = farmYearInputs?.[year]?.practiceTags ?? [];

              return (
                <tr
                  key={year}
                  className={`hover:bg-rowHover ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                >
                  <td className="w-16 py-1.5 pl-2 pr-4 text-base font-semibold text-charcoal">
                    {year}
                    {row && (
                      <LinkButton
                        className="block whitespace-nowrap text-xs"
                        onClick={() =>
                          navigateToFarmsView({
                            nodeKey: `${PADDOCK_KEY_PREFIX}${paddockId}`,
                            tab: INPUT_DOC_TAB_PLANNING,
                            year,
                          })
                        }
                      >
                        {LINK_OPEN_PLAN_LABEL}
                      </LinkButton>
                    )}
                  </td>
                  <td className="w-24 px-2 py-1.5">
                    <NumericInputField
                      ref={(el) => {
                        rainfallInputRefs.current[idx] = el;
                      }}
                      min={0}
                      controls={false}
                      unit="mm"
                      unitClassName="-ml-2 self-stretch flex items-center rounded-r border border-l-0 border-gray-300 bg-gray-50 px-2 text-sm text-gray-muted"
                      inputClassName="w-16 rounded-r-none"
                      value={rainfallMm}
                      onChange={(value) =>
                        dispatch(setFarmYearRainfall({ farmId, year, rainfallMm: value }))
                      }
                      onKeyDown={(event) => {
                        if (event.key === 'ArrowUp') {
                          event.preventDefault();
                          rainfallInputRefs.current[idx - 1]?.focus();
                        } else if (event.key === 'ArrowDown') {
                          event.preventDefault();
                          rainfallInputRefs.current[idx + 1]?.focus();
                        }
                      }}
                    />
                  </td>
                  <td className="w-44 px-2 py-1.5">
                    <Select
                      mode="multiple"
                      size="small"
                      className="w-40"
                      placeholder={FARM_YEAR_INPUTS_PRACTICE_SELECT_PLACEHOLDER}
                      value={practiceTags}
                      options={PRACTICE_OPTIONS}
                      onChange={(newSelection: PracticeTag[]) =>
                        dispatch(
                          setFarmYearPracticeTags({
                            farmId,
                            year,
                            tags: resolvePracticeTagsAfterChange(practiceTags, newSelection),
                          })
                        )
                      }
                    />
                  </td>
                  <td className="w-32 border-l-2 border-gray-bright px-2 py-1.5 pl-4">
                    {row ? (
                      <Segmented<InputTaskCategory>
                        size="small"
                        value={activeCategory}
                        onChange={(value) => onCategoryChange(row.year, value)}
                        options={CATEGORY_OPTIONS.map((opt) => ({
                          ...opt,
                          disabled:
                            opt.value === InputTaskCategory.Actual
                              ? !row.actual
                              : !row.plan,
                        }))}
                      />
                    ) : (
                      <NoDataCell reason="No data recorded for this source" />
                    )}
                  </td>
                  <td className="w-20 px-2 py-1.5 text-right tabular-nums text-charcoal">
                    {result ? (
                      formatNumber(result.areaHa)
                    ) : (
                      <NoDataCell reason="No data recorded for this source" />
                    )}
                  </td>
                  <td className="w-28 px-2 py-1.5">
                    {result ? (
                      <div className="flex flex-col gap-1">
                        <span
                          className="truncate text-xs text-gray-muted"
                          title={`${FARM_DIAGNOSTICS_CROP_LABEL}: ${row?.crop}`}
                        >
                          {row?.crop}
                        </span>
                        <NumericInputField
                          min={0}
                          inputClassName="w-24"
                          value={yearOverride?.yieldOverride ?? result.yieldValue}
                          onChange={(value) =>
                            value != null &&
                            dispatch(
                              setFarmDiagnosticsYieldOverride({
                                paddockId,
                                year,
                                value,
                              })
                            )
                          }
                        />
                      </div>
                    ) : (
                      <NoDataCell reason="No data recorded for this source" />
                    )}
                  </td>
                  {showProduction && (
                    <td className="w-24 px-2 py-1.5 text-right tabular-nums text-charcoal">
                      {production != null ? (
                        formatNumber(production)
                      ) : (
                        <NoDataCell reason="No data recorded for this source" />
                      )}
                    </td>
                  )}
                  {showIncome && (
                    <td className="w-24 px-2 py-1.5 text-right tabular-nums text-charcoal">
                      {income != null ? (
                        formatCurrency(income)
                      ) : (
                        <NoDataCell reason="No data recorded for this source" />
                      )}
                    </td>
                  )}
                  {showTotalCost && (
                    <td className="w-28 px-2 py-1.5 text-right tabular-nums text-charcoal">
                      {totalCostDisplay != null ? (
                        <Tooltip title={FARM_DIAGNOSTICS_TOTAL_COST_TOOLTIP}>
                          <span className="cursor-help">
                            {formatCurrency(totalCostDisplay)}
                          </span>
                        </Tooltip>
                      ) : (
                        <NoDataCell reason="No data recorded for this source" />
                      )}
                    </td>
                  )}
                  {visibleCostCategories.map((category) => {
                    if (!result) {
                      return (
                        <td key={category} className="w-36 px-2 py-1.5">
                          <NoDataCell reason="No data recorded for this source" />
                        </td>
                      );
                    }
                    const baseTotal = result.costsTotal[category] ?? 0;
                    const baseCostPerHa =
                      result.areaHa > 0 ? baseTotal / result.areaHa : 0;
                    const overriddenTotal = yearOverride?.costOverrides?.[category];
                    const displayValue =
                      costDisplayMode === 'total'
                        ? (overriddenTotal ?? baseTotal)
                        : overriddenTotal != null
                          ? result.areaHa > 0
                            ? overriddenTotal / result.areaHa
                            : 0
                          : baseCostPerHa;

                    return (
                      <td key={category} className="w-36 px-2 py-1.5">
                        <NumericInputField
                          min={0}
                          precision={COST_PRECISION}
                          step={COST_STEP}
                          inputClassName="w-28"
                          value={displayValue}
                          onChange={(value) => {
                            if (value == null) return;
                            const totalValue =
                              costDisplayMode === 'total'
                                ? value
                                : value * result.areaHa;
                            dispatch(
                              setFarmDiagnosticsCostOverride({
                                paddockId,
                                year,
                                category,
                                value: totalValue,
                              })
                            );
                          }}
                        />
                      </td>
                    );
                  })}
                  <td className="w-24 px-2 py-1.5">
                    {hasOverride && (
                      <AntdButton
                        size="small"
                        type="link"
                        onClick={() =>
                          dispatch(clearFarmDiagnosticsYearOverrides({ paddockId, year }))
                        }
                      >
                        {FARM_DIAGNOSTICS_RESET_YEAR_LABEL}
                      </AntdButton>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td className="py-1.5 pl-2 pr-4 text-charcoal" colSpan={3} />
              <td className="border-l-2 border-gray-bright py-1.5 pl-4 pr-4 text-right text-base font-bold text-charcoal">
                Total:
              </td>
              <td className="w-20 px-2 py-1.5 text-right font-bold tabular-nums text-charcoal">
                {formatNumber(totals.areaHa)}
              </td>
              <td className="w-28 px-2 py-1.5" />
              {showProduction && (
                <td className="w-24 px-2 py-1.5 text-right font-bold tabular-nums text-charcoal">
                  {formatNumber(totals.production)}
                </td>
              )}
              {showIncome && (
                <td className="w-24 px-2 py-1.5 text-right font-bold tabular-nums text-charcoal">
                  {formatCurrency(totals.incomeAvgPerHa)}
                  <Tooltip title={FARM_DIAGNOSTICS_TOTAL_ROW_AVG_TOOLTIP}>
                    <span className="ml-1 cursor-help text-xs font-normal text-gray-muted">
                      {FARM_DIAGNOSTICS_TOTAL_ROW_AVG_LABEL}
                    </span>
                  </Tooltip>
                </td>
              )}
              {showTotalCost && (
                <td className="w-28 px-2 py-1.5 text-right font-bold tabular-nums text-charcoal">
                  {formatCurrency(toFooterCost(totals.totalCost))}
                  {avgBadge}
                </td>
              )}
              {visibleCostCategories.map((category) => (
                <td
                  key={category}
                  className="w-36 px-2 py-1.5 text-right font-bold tabular-nums text-charcoal"
                >
                  {formatCurrency(toFooterCost(totals.byCategory[category] ?? 0))}
                  {avgBadge}
                </td>
              ))}
              <td className="w-24 px-2 py-1.5" />
            </tr>
          </tfoot>
        </table>
      </div>
      )}
    </SectionCard>
  );
};

export default FarmDiagnosticsTable;
